import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  XMarkIcon,
  CalendarIcon,
  EyeIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ClockIcon,
  MapPinIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { CalendarIcon as CalendarSolidIcon } from '@heroicons/react/24/solid';
import intranetApi from '../../services/intranetApi';
import api from '../../utils/api';

const CreateEvent = ({ isOpen, onClose, onEventCreated, onEventUpdated, event = null }) => {
  // DEBUG: Use window object since console.log gets stripped in production
  window.DEBUG_CreateEvent = window.DEBUG_CreateEvent || [];
  window.DEBUG_CreateEvent.push('🚀 CreateEvent component loaded - DEPLOYMENT TEST');
  
  // COMPREHENSIVE DEBUG LOGGING
  window.DEBUG_CreateEvent.push('=== CreateEvent Debug Info ===');
  window.DEBUG_CreateEvent.push(`isOpen: ${isOpen}`);
  window.DEBUG_CreateEvent.push(`event prop: ${JSON.stringify(event)}`);
  window.DEBUG_CreateEvent.push(`event type: ${typeof event}`);
  window.DEBUG_CreateEvent.push(`event keys: ${event ? Object.keys(event).join(', ') : 'null'}`);
  if (event) {
    window.DEBUG_CreateEvent.push(`event.event_type_id: ${event.event_type_id} (${typeof event.event_type_id})`);
    window.DEBUG_CreateEvent.push(`event.description: "${event.description}" (${typeof event.description})`);
    window.DEBUG_CreateEvent.push(`event.start_datetime: ${event.start_datetime} (${typeof event.start_datetime})`);
    window.DEBUG_CreateEvent.push(`event.end_datetime: ${event.end_datetime} (${typeof event.end_datetime})`);
  }
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type_id: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    location: '',
    max_attendees: '',
    visibility: 'company',
    department_id: '',
    rsvp_deadline_date: '',
    rsvp_deadline_time: ''
  });

  const [eventTypes, setEventTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [previewMode, setPreviewMode] = useState(false);

  const isEditMode = !!event;

  // Debug logging for event prop changes
  useEffect(() => {
    window.DEBUG_CreateEvent.push(`Event prop changed: ${JSON.stringify(event)}`);
    window.DEBUG_CreateEvent.push(`isEditMode: ${isEditMode}`);
  }, [event, isEditMode]);

  // Initialize form data when event changes (for edit mode)
  useEffect(() => {
    if (event && isOpen) {
      // Initialize form data regardless of event types loading status
      // We'll handle event type validation separately
      window.DEBUG_CreateEvent.push('=== FORM INITIALIZATION START ===');
      window.DEBUG_CreateEvent.push(`Initializing form data for edit mode: ${JSON.stringify(event)}`);
      window.DEBUG_CreateEvent.push(`Available event types: ${JSON.stringify(eventTypes.map(et => ({id: et.id, label: et.display_label})))}`);
      
      // DETAILED FIELD DEBUGGING
      window.DEBUG_CreateEvent.push(`Raw event.title: "${event.title}"`);
      window.DEBUG_CreateEvent.push(`Raw event.description: ${JSON.stringify(event.description)} (type: ${typeof event.description})`);
      window.DEBUG_CreateEvent.push(`Raw event.event_type_id: ${JSON.stringify(event.event_type_id)} (type: ${typeof event.event_type_id})`);
      window.DEBUG_CreateEvent.push(`Raw event.start_datetime: "${event.start_datetime}"`);
      window.DEBUG_CreateEvent.push(`Raw event.end_datetime: "${event.end_datetime}"`);
      
      // Handle description - check for null, undefined, or empty string
      let descriptionValue = '';
      if (event.description !== null && event.description !== undefined) {
        descriptionValue = String(event.description);
      }
      
      // Handle event_type_id - check for null, undefined, or 0
      let eventTypeValue = '';
      if (event.event_type_id !== null && event.event_type_id !== undefined && event.event_type_id !== 0) {
        eventTypeValue = String(event.event_type_id);
      }
      
      window.DEBUG_CreateEvent.push(`Processed description: "${descriptionValue}" (length: ${descriptionValue.length})`);
      window.DEBUG_CreateEvent.push(`Processed event_type_id: "${eventTypeValue}"`);
      
      // Extract date and time directly from datetime strings to avoid timezone conversion issues
      // Following the exact same pattern used in UpcomingEvents.js, CompanyCalendar.js, HolidayManagement.js
      const getDateFromDatetime = (datetimeString) => {
        if (!datetimeString) return '';
        window.DEBUG_CreateEvent.push(`Parsing date from: "${datetimeString}"`);
        
        // Extract date portion from datetime string (format: YYYY-MM-DDTHH:MM:SS or YYYY-MM-DD HH:MM:SS)
        // Split by 'T' first, then by space as fallback - same as other components
        let datePart;
        if (datetimeString.includes('T')) {
          datePart = datetimeString.split('T')[0];
        } else if (datetimeString.includes(' ')) {
          datePart = datetimeString.split(' ')[0];
        } else {
          datePart = datetimeString; // Assume it's already just the date part
        }
        
        window.DEBUG_CreateEvent.push(`Extracted date part: "${datePart}"`);
        return datePart || '';
      };

      // Extract time directly from datetime string - no Date object creation
      const getTimeFromDatetime = (datetimeString) => {
        if (!datetimeString) return '';
        window.DEBUG_CreateEvent.push(`Parsing time from: "${datetimeString}"`);
        
        // Extract time portion from datetime string (format: YYYY-MM-DDTHH:MM:SS or YYYY-MM-DD HH:MM:SS)
        let timePart;
        if (datetimeString.includes('T')) {
          timePart = datetimeString.split('T')[1];
        } else if (datetimeString.includes(' ')) {
          timePart = datetimeString.split(' ')[1];
        } else {
          return ''; // No time part found
        }
        
        // Extract HH:MM from HH:MM:SS format
        const timeOnly = timePart ? timePart.slice(0, 5) : '';
        window.DEBUG_CreateEvent.push(`Extracted time part: "${timeOnly}"`);
        return timeOnly;
      };

      const newFormData = {
        title: event.title || '',
        description: descriptionValue,
        event_type_id: eventTypeValue,
        start_date: getDateFromDatetime(event.start_datetime),
        start_time: getTimeFromDatetime(event.start_datetime),
        end_date: getDateFromDatetime(event.end_datetime),
        end_time: getTimeFromDatetime(event.end_datetime),
        location: event.location || '',
        max_attendees: event.max_attendees ? event.max_attendees.toString() : '',
        visibility: event.visibility || 'company',
        department_id: event.department_id ? event.department_id.toString() : '',
        rsvp_deadline_date: getDateFromDatetime(event.rsvp_deadline),
        rsvp_deadline_time: getTimeFromDatetime(event.rsvp_deadline)
      };

      window.DEBUG_CreateEvent.push('=== FORM DATA MAPPING ===');
      window.DEBUG_CreateEvent.push(`event.title -> "${newFormData.title}"`);
      window.DEBUG_CreateEvent.push(`event.description -> "${newFormData.description}"`);
      window.DEBUG_CreateEvent.push(`event.event_type_id (${event.event_type_id}) -> "${newFormData.event_type_id}"`);
      window.DEBUG_CreateEvent.push(`event.start_datetime (${event.start_datetime}) -> date:"${newFormData.start_date}" time:"${newFormData.start_time}"`);
      window.DEBUG_CreateEvent.push(`event.end_datetime (${event.end_datetime}) -> date:"${newFormData.end_date}" time:"${newFormData.end_time}"`);
      window.DEBUG_CreateEvent.push(`Final form data: ${JSON.stringify(newFormData)}`);
      window.DEBUG_CreateEvent.push('=== FORM INITIALIZATION END ===');

      setFormData(newFormData);
    }
  }, [event, isOpen]); // Removed eventTypes dependency to avoid timing issues

  // Fetch initial data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchInitialData();
    }
  }, [isOpen]);

  // Set default datetime only for new events after initial data is loaded
  useEffect(() => {
    if (isOpen && !isEditMode && eventTypes.length > 0 && !formData.start_date && !event) {
      window.DEBUG_CreateEvent.push('Setting default datetime for new event');
      setDefaultDateTime();
    }
  }, [isOpen, isEditMode, eventTypes, formData.start_date, event]);

  const setDefaultDateTime = () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0); // Default to 9 AM tomorrow

    const defaultDate = tomorrow.toISOString().split('T')[0];
    const defaultTime = '09:00';

    setFormData(prev => ({
      ...prev,
      start_date: defaultDate,
      start_time: defaultTime,
      end_date: defaultDate,
      end_time: '10:00'
    }));
  };

  const fetchInitialData = async () => {
    try {
      window.DEBUG_CreateEvent.push('Fetching initial data...');
      const [eventTypesResponse, departmentsResponse, userResponse] = await Promise.all([
        intranetApi.config.getEventTypes(),
        api.get('/departments'),
        api.get('/auth/me')
      ]);

      window.DEBUG_CreateEvent.push(`Initial data fetched: eventTypes:${eventTypesResponse.data.event_types?.length}, departments:${departmentsResponse.data.departments?.length}, user:${userResponse.data.user?.id}`);

      setEventTypes(eventTypesResponse.data.event_types || []);
      setDepartments(departmentsResponse.data.departments || []);
      setCurrentUser(userResponse.data.user);
    } catch (error) {
      window.DEBUG_CreateEvent.push(`Error fetching initial data: ${error.message}`);
      toast.error('Failed to load form data');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Auto-select department for department events
    if (name === 'visibility' && value === 'department' && currentUser?.department_id) {
      setFormData(prev => ({
        ...prev,
        department_id: currentUser.department_id.toString()
      }));
    }

    // Clear department when switching to company-wide
    if (name === 'visibility' && value === 'company') {
      setFormData(prev => ({
        ...prev,
        department_id: ''
      }));
    }

    // Auto-adjust end date/time when start date/time changes
    if (name === 'start_date') {
      setFormData(prev => ({
        ...prev,
        end_date: value // Default end date to same as start date
      }));
    }

    if (name === 'start_time' && formData.end_time <= value) {
      // Auto-adjust end time to be 1 hour after start time
      const startTime = new Date(`2000-01-01T${value}:00`);
      startTime.setHours(startTime.getHours() + 1);
      // Extract time in HH:MM format without timezone conversion
      const hours = startTime.getHours().toString().padStart(2, '0');
      const minutes = startTime.getMinutes().toString().padStart(2, '0');
      const endTime = `${hours}:${minutes}`;
      
      setFormData(prev => ({
        ...prev,
        end_time: endTime
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Event title is required';
    } else if (formData.title.length > 200) {
      newErrors.title = 'Title must be less than 200 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Event description is required';
    } else if (formData.description.length > 2000) {
      newErrors.description = 'Description must be less than 2,000 characters';
    }

    if (!formData.event_type_id) {
      newErrors.event_type_id = 'Event type is required';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.start_time) {
      newErrors.start_time = 'Start time is required';
    }

    // Validate that start date/time is in the future (only for new events)
    if (!isEditMode && formData.start_date && formData.start_time) {
      const startDateTime = new Date(`${formData.start_date}T${formData.start_time}`);
      if (startDateTime <= new Date()) {
        newErrors.start_date = 'Event must be scheduled for a future date and time';
      }
    }

    // Validate end date/time if provided
    if (formData.end_date && formData.end_time && formData.start_date && formData.start_time) {
      const startDateTime = new Date(`${formData.start_date}T${formData.start_time}`);
      const endDateTime = new Date(`${formData.end_date}T${formData.end_time}`);
      
      if (endDateTime <= startDateTime) {
        newErrors.end_time = 'End time must be after start time';
      }
    }

    // Validate RSVP deadline if provided
    if (formData.rsvp_deadline_date && formData.rsvp_deadline_time && formData.start_date && formData.start_time) {
      const rsvpDeadline = new Date(`${formData.rsvp_deadline_date}T${formData.rsvp_deadline_time}`);
      const startDateTime = new Date(`${formData.start_date}T${formData.start_time}`);
      
      if (rsvpDeadline >= startDateTime) {
        newErrors.rsvp_deadline_date = 'RSVP deadline must be before event start time';
      }
    }

    if (formData.visibility === 'department' && !formData.department_id) {
      newErrors.department_id = 'Department is required for department events';
    }

    if (formData.max_attendees && (isNaN(formData.max_attendees) || parseInt(formData.max_attendees) < 1)) {
      newErrors.max_attendees = 'Max attendees must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare datetime strings for backend
      const start_datetime = `${formData.start_date}T${formData.start_time}:00`;
      const end_datetime = formData.end_date && formData.end_time 
        ? `${formData.end_date}T${formData.end_time}:00` 
        : null;
      const rsvp_deadline = formData.rsvp_deadline_date && formData.rsvp_deadline_time
        ? `${formData.rsvp_deadline_date}T${formData.rsvp_deadline_time}:00`
        : null;

      const eventData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        event_type_id: parseInt(formData.event_type_id),
        start_datetime,
        end_datetime,
        location: formData.location.trim() || null,
        max_attendees: formData.max_attendees ? parseInt(formData.max_attendees) : null,
        visibility: formData.visibility,
        department_id: formData.department_id ? parseInt(formData.department_id) : null,
        rsvp_deadline
      };

      let response;
      if (isEditMode) {
        response = await intranetApi.events.updateEvent(event.id, eventData);
      } else {
        response = await intranetApi.events.createEvent(eventData);
      }
      
      if (response.data.success) {
        toast.success(isEditMode ? 'Event updated successfully!' : 'Event created successfully!');
        if (isEditMode && onEventUpdated) {
          onEventUpdated(response.data.event);
        } else if (!isEditMode && onEventCreated) {
          onEventCreated(response.data.event);
        }
        handleClose();
      } else {
        throw new Error(response.data.error || `Failed to ${isEditMode ? 'update' : 'create'} event`);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} event:`, error);
      toast.error(error.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} event`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!isEditMode) {
      setFormData({
        title: '',
        description: '',
        event_type_id: '',
        start_date: '',
        start_time: '',
        end_date: '',
        end_time: '',
        location: '',
        max_attendees: '',
        visibility: 'company',
        department_id: '',
        rsvp_deadline_date: '',
        rsvp_deadline_time: ''
      });
    }
    setErrors({});
    setPreviewMode(false);
    onClose();
  };

  const formatPreviewContent = (content) => {
    return content.split('\n').map((line, index) => (
      <p key={index} className="mb-2 last:mb-0">
        {line || '\u00A0'}
      </p>
    ));
  };

  const getSelectedEventType = () => {
    if (!formData.event_type_id) return null;
    return eventTypes.find(type => type.id.toString() === formData.event_type_id);
  };

  const getSelectedDepartmentName = () => {
    if (!formData.department_id) return null;
    const department = departments.find(dept => dept.id.toString() === formData.department_id);
    return department?.name;
  };

  const formatDateTime = (date, time) => {
    if (!date || !time) return '';
    
    // FIXED: Don't create Date objects to avoid timezone conversion
    // Instead, format the date and time directly from the string components
    try {
      // Parse date components
      const [year, month, day] = date.split('-');
      const [hours, minutes] = time.split(':');
      
      // Create date object in local timezone (not UTC)
      const dateTime = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes));
      
      return dateTime.toLocaleString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date/time:', error, { date, time });
      return `${date} ${time}`;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center">
            <CalendarIcon className="h-6 w-6 text-indigo-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditMode ? 'Edit Event' : 'Create New Event'}
            </h2>
          </div>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => setPreviewMode(!previewMode)}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors duration-200"
            >
              <EyeIcon className="h-4 w-4 mr-2" />
              {previewMode ? 'Edit' : 'Preview'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content - Made scrollable */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {previewMode ? (
            /* Preview Mode */
            <div className="p-6">
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="p-6">
                  {/* Event Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="flex-shrink-0 h-12 w-12 rounded-lg flex items-center justify-center text-white"
                        style={{ backgroundColor: getSelectedEventType()?.color || '#6B7280' }}
                      >
                        <CalendarSolidIcon className="h-6 w-6" />
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">
                          {formData.title || 'Event Title'}
                        </h4>
                        <p className="text-sm text-gray-500">
                          {getSelectedEventType()?.display_label || 'Event Type'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {formData.visibility === 'department' ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <UserGroupIcon className="h-3 w-3 mr-1" />
                          {getSelectedDepartmentName() || 'Department'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <BuildingOfficeIcon className="h-3 w-3 mr-1" />
                          Company-wide
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Event Details */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      {formatDateTime(formData.start_date, formData.start_time) || 'Event date and time'}
                    </div>
                    
                    {formData.end_date && formData.end_time && (
                      <div className="flex items-center text-sm text-gray-600">
                        <ClockIcon className="h-4 w-4 mr-2" />
                        Ends: {formatDateTime(formData.end_date, formData.end_time)}
                      </div>
                    )}
                    
                    {formData.location && (
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPinIcon className="h-4 w-4 mr-2" />
                        {formData.location}
                      </div>
                    )}

                    {formData.max_attendees && (
                      <div className="flex items-center text-sm text-gray-600">
                        <UserGroupIcon className="h-4 w-4 mr-2" />
                        Max attendees: {formData.max_attendees}
                      </div>
                    )}

                    {formData.rsvp_deadline_date && formData.rsvp_deadline_time && (
                      <div className="flex items-center text-sm text-gray-600">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                        RSVP by: {formatDateTime(formData.rsvp_deadline_date, formData.rsvp_deadline_time)}
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div className="mb-4">
                    <h5 className="text-sm font-medium text-gray-900 mb-2">Description</h5>
                    <div className="text-gray-700 text-sm leading-relaxed">
                      {formData.description ? formatPreviewContent(formData.description) : (
                        <p className="text-gray-400 italic">Event description will appear here...</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Form Mode */
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
                
                {/* Title */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Event Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                      errors.title ? 'border-red-300' : ''
                    }`}
                    placeholder="Enter event title..."
                    maxLength={200}
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.title.length}/200 characters
                  </p>
                </div>

                {/* Event Type */}
                <div>
                  <label htmlFor="event_type_id" className="block text-sm font-medium text-gray-700 mb-1">
                    Event Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="event_type_id"
                    name="event_type_id"
                    value={formData.event_type_id}
                    onChange={handleInputChange}
                    className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                      errors.event_type_id ? 'border-red-300' : ''
                    }`}
                  >
                    <option value="">Select event type...</option>
                    {eventTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.display_label}
                      </option>
                    ))}
                  </select>
                  {errors.event_type_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.event_type_id}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={4}
                    value={formData.description}
                    onChange={handleInputChange}
                    className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                      errors.description ? 'border-red-300' : ''
                    }`}
                    placeholder="Describe the event, agenda, what to expect..."
                    maxLength={2000}
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.description.length}/2,000 characters
                  </p>
                </div>
              </div>

              {/* Date and Time */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Date & Time</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Start Date */}
                  <div>
                    <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="start_date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleInputChange}
                      className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                        errors.start_date ? 'border-red-300' : ''
                      }`}
                    />
                    {errors.start_date && (
                      <p className="mt-1 text-sm text-red-600">{errors.start_date}</p>
                    )}
                  </div>

                  {/* Start Time */}
                  <div>
                    <label htmlFor="start_time" className="block text-sm font-medium text-gray-700 mb-1">
                      Start Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      id="start_time"
                      name="start_time"
                      value={formData.start_time}
                      onChange={handleInputChange}
                      className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                        errors.start_time ? 'border-red-300' : ''
                      }`}
                    />
                    {errors.start_time && (
                      <p className="mt-1 text-sm text-red-600">{errors.start_time}</p>
                    )}
                  </div>

                  {/* End Date */}
                  <div>
                    <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-1">
                      End Date <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="date"
                      id="end_date"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>

                  {/* End Time */}
                  <div>
                    <label htmlFor="end_time" className="block text-sm font-medium text-gray-700 mb-1">
                      End Time <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="time"
                      id="end_time"
                      name="end_time"
                      value={formData.end_time}
                      onChange={handleInputChange}
                      className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                        errors.end_time ? 'border-red-300' : ''
                      }`}
                    />
                    {errors.end_time && (
                      <p className="mt-1 text-sm text-red-600">{errors.end_time}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Event Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Event Details</h3>
                
                {/* Location */}
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                    Location <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Conference room, address, or virtual meeting link..."
                  />
                </div>

                {/* Max Attendees */}
                <div>
                  <label htmlFor="max_attendees" className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Attendees <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="number"
                    id="max_attendees"
                    name="max_attendees"
                    value={formData.max_attendees}
                    onChange={handleInputChange}
                    min="1"
                    className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                      errors.max_attendees ? 'border-red-300' : ''
                    }`}
                    placeholder="Leave empty for unlimited"
                  />
                  {errors.max_attendees && (
                    <p className="mt-1 text-sm text-red-600">{errors.max_attendees}</p>
                  )}
                </div>

                {/* RSVP Deadline */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="rsvp_deadline_date" className="block text-sm font-medium text-gray-700 mb-1">
                      RSVP Deadline Date <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="date"
                      id="rsvp_deadline_date"
                      name="rsvp_deadline_date"
                      value={formData.rsvp_deadline_date}
                      onChange={handleInputChange}
                      className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                        errors.rsvp_deadline_date ? 'border-red-300' : ''
                      }`}
                    />
                    {errors.rsvp_deadline_date && (
                      <p className="mt-1 text-sm text-red-600">{errors.rsvp_deadline_date}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="rsvp_deadline_time" className="block text-sm font-medium text-gray-700 mb-1">
                      RSVP Deadline Time <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="time"
                      id="rsvp_deadline_time"
                      name="rsvp_deadline_time"
                      value={formData.rsvp_deadline_time}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Visibility Settings */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Visibility</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      id="visibility-company"
                      name="visibility"
                      type="radio"
                      value="company"
                      checked={formData.visibility === 'company'}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <label htmlFor="visibility-company" className="ml-3 flex items-center">
                      <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-700">Company-wide</span>
                      <span className="ml-2 text-xs text-gray-500">Visible to all employees</span>
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="visibility-department"
                      name="visibility"
                      type="radio"
                      value="department"
                      checked={formData.visibility === 'department'}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <label htmlFor="visibility-department" className="ml-3 flex items-center">
                      <UserGroupIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-700">Department only</span>
                      <span className="ml-2 text-xs text-gray-500">Visible to department members only</span>
                    </label>
                  </div>
                </div>

                {/* Department Selection */}
                {formData.visibility === 'department' && (
                  <div>
                    <label htmlFor="department_id" className="block text-sm font-medium text-gray-700 mb-1">
                      Department <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="department_id"
                      name="department_id"
                      value={formData.department_id}
                      onChange={handleInputChange}
                      className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                        errors.department_id ? 'border-red-300' : ''
                      }`}
                    >
                      <option value="">Select department...</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                    {errors.department_id && (
                      <p className="mt-1 text-sm text-red-600">{errors.department_id}</p>
                    )}
                  </div>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="text-sm text-gray-500">
            {previewMode ? 'Preview mode - review your event details' : `Fill out the form to ${isEditMode ? 'update' : 'create'} your event`}
          </div>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            {!previewMode && (
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isEditMode ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {isEditMode ? 'Update Event' : 'Create Event'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateEvent; 